const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Resumen de cobro para factura u orden (opcional factura vinculada en órdenes).
 */
export const getErpPaymentSummary = (doc, type, linkedFactura = null) => {
  if (!doc) return null;

  const symbol = doc.tenantCurrency?.symbol || "C$";
  const total = toNumber(doc.total);
  const voided = doc.anulado === true;
  const cancelled = doc.cancelado === true;

  let totalAbonado = 0;
  let saldoPendiente = 0;
  let pagos = [];
  let source = "documento";

  if (type === "facturas-venta") {
    saldoPendiente = toNumber(doc.saldoPendiente ?? doc.saldo);
    totalAbonado = toNumber(doc.totalAbonado);
    if (totalAbonado <= 0 && total > 0 && saldoPendiente < total) {
      totalAbonado = Math.max(0, total - saldoPendiente);
    }
    pagos = Array.isArray(doc.pagos) ? doc.pagos : [];
  } else if (linkedFactura) {
    source = "factura";
    saldoPendiente = toNumber(
      linkedFactura.saldoPendiente ?? linkedFactura.saldo
    );
    totalAbonado = toNumber(linkedFactura.totalAbonado);
    if (totalAbonado <= 0 && total > 0 && saldoPendiente < total) {
      totalAbonado = Math.max(0, toNumber(linkedFactura.total) - saldoPendiente);
    }
    pagos = Array.isArray(linkedFactura.pagos) ? linkedFactura.pagos : [];
  } else {
    totalAbonado = 0;
    saldoPendiente = 0;
    pagos = [];
  }

  const isPaid =
    !voided &&
    !cancelled &&
    (saldoPendiente <= 0.009 || total <= 0);
  const isPartial =
    !voided &&
    !cancelled &&
    totalAbonado > 0.009 &&
    saldoPendiente > 0.009;
  const isPending = !voided && !cancelled && saldoPendiente > 0.009;

  let status = "pendiente";
  let statusLabel = "Pendiente de pago";
  if (voided) {
    status = "anulada";
    statusLabel = "Anulada";
  } else if (cancelled) {
    status = "cancelada";
    statusLabel = "Cancelada";
  } else if (isPaid) {
    status = "pagada";
    statusLabel =
      type === "ordenes" && !linkedFactura && !doc.facturaVentaId
        ? "Sin saldo pendiente"
        : "Pagada";
  } else if (isPartial) {
    status = "parcial";
    statusLabel = "Pago parcial";
  }

  return {
    symbol,
    total,
    totalAbonado,
    saldoPendiente,
    pagos,
    source,
    status,
    statusLabel,
    isPaid,
    isPartial,
    isPending,
    voided,
    cancelled,
    linkedFacturaId:
      type === "ordenes" ? doc.facturaVentaId || linkedFactura?.facturaVentaId : null,
    linkedFacturaNumero:
      linkedFactura?.numeroFactura != null
        ? linkedFactura.numeroFactura
        : doc.facturaVenta?.numeroFactura
  };
};

/** Saldo pendiente en listado de facturas (`saldo` null = pagada). */
export const getFacturaSaldoFromListRow = (row) => {
  if (!row) return 0;
  if (row.saldo === null || row.saldo === undefined || row.saldo === "") {
    return 0;
  }
  return toNumber(row.saldo);
};

/** Resumen rápido desde fila de listado (facturas traen `saldo`). */
export const getErpPaymentSummaryFromListRow = (row, type, facturaSaldoById) => {
  if (!row) return null;
  if (type === "facturas-venta") {
    const total = toNumber(row.total);
    const saldoPendiente = getFacturaSaldoFromListRow(row);
    const totalAbonado =
      total > 0 && saldoPendiente < total ? total - saldoPendiente : 0;
    const voided = row.anulado === true;
    const isPaid = !voided && saldoPendiente <= 0.009 && total > 0;
    const isPartial =
      !voided && totalAbonado > 0.009 && saldoPendiente > 0.009;

    let statusLabel = "Pendiente";
    if (voided) statusLabel = "Anulada";
    else if (isPaid) statusLabel = "Pagada";
    else if (isPartial) statusLabel = "Parcial";

    return {
      symbol: row.tenantCurrency?.symbol || "C$",
      total,
      totalAbonado,
      saldoPendiente,
      statusLabel,
      voided,
      isPending: !voided && saldoPendiente > 0.009
    };
  }

  const fvId = row.facturaVentaId ? Number(row.facturaVentaId) : null;
  const linkedSaldo =
    fvId && facturaSaldoById && facturaSaldoById.has(fvId)
      ? facturaSaldoById.get(fvId)
      : null;

  if (fvId && linkedSaldo !== null) {
    const total = toNumber(row.total);
    const saldoPendiente = linkedSaldo;
    const isPaid = saldoPendiente <= 0.009;
    const totalAbonado =
      total > 0 && saldoPendiente < total ? total - saldoPendiente : isPaid ? total : 0;
    return {
      symbol: row.tenantCurrency?.symbol || "C$",
      total,
      totalAbonado,
      saldoPendiente,
      statusLabel: isPaid ? "Pagada" : saldoPendiente < total ? "Parcial" : "Pendiente",
      isPending: saldoPendiente > 0.009
    };
  }

  if (fvId) {
    return {
      symbol: row.tenantCurrency?.symbol || "C$",
      total: toNumber(row.total),
      statusLabel: "Facturada",
      saldoPendiente: null,
      isPending: false
    };
  }

  return {
    symbol: row.tenantCurrency?.symbol || "C$",
    total: toNumber(row.total),
    saldoPendiente: 0,
    totalAbonado: 0,
    statusLabel: "Sin factura",
    isPending: false
  };
};

export default getErpPaymentSummary;
